import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Info, MessageSquareReply, Star } from 'lucide-react';
import { merchantExperienceApi, extractErrorMessage, formatDateTime, formatNumber } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type { MerchantExperienceReviewDto } from '@kardume/types';
import { EXPERIENCE_REVIEW_ERROR_MESSAGES } from '@kardume/types';
import { Badge, Button, Modal, Pagination, StatCard } from '@kardume/ui';
import { useAsync } from '../../utils/useAsync';
import DataState from '../DataState';

/**
 * As avaliações das EXPERIÊNCIAS da loja (card #348).
 *
 * Seção própria, e não uma aba dentro de "Avaliações": a nota da experiência
 * responde "a degustação valeu?", e a da loja responde "gostei do lugar?".
 * Somá-las num indicador só apagaria as duas perguntas - por isso a média aqui
 * é calculada apenas sobre o que esta rota devolve, e nunca entra na média da
 * loja.
 *
 * A resposta é uma só e não edita o texto do cliente: quem escreveu continua
 * dono do que escreveu.
 */

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Notice = styled.div`
  display: flex;
  gap: 0.55rem;
  align-items: flex-start;
  padding: 0.7rem 0.85rem;
  margin-bottom: 1rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.82rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  svg {
    flex-shrink: 0;
    margin-top: 0.1rem;
    color: ${(p) => p.theme.primary};
  }
`;

const List = styled.div`
  display: grid;
  gap: 0.85rem;
`;

const Item = styled.article`
  padding: 1rem 1.1rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radiusLg};
  background: ${(p) => p.theme.surface};
  box-shadow: ${(p) => p.theme.shadowSm};
  display: grid;
  gap: 0.55rem;
`;

const ItemHead = styled.header`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;

  strong {
    color: ${(p) => p.theme.textPrimary};
  }
  .when {
    margin-left: auto;
    font-size: 0.78rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const Stars = styled.span`
  display: inline-flex;
  gap: 0.1rem;
  color: ${(p) => p.theme.accent};
`;

const Comment = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.55;
  color: ${(p) => p.theme.textPrimary};
  white-space: pre-wrap;
`;

const Reply = styled.div`
  padding: 0.7rem 0.85rem;
  border-left: 3px solid ${(p) => p.theme.primary};
  background: ${(p) => p.theme.surfaceAlt};
  border-radius: 0 ${(p) => p.theme.radius} ${(p) => p.theme.radius} 0;
  font-size: 0.86rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  .label {
    display: block;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${(p) => p.theme.textMuted};
    margin-bottom: 0.25rem;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
  color: ${(p) => p.theme.textSecondary};
`;

const Textarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 120px;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
  font-family: inherit;
  color: ${(p) => p.theme.textPrimary};
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.primary};
  }
`;

const Counter = styled.div`
  margin-top: 0.25rem;
  text-align: right;
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
  color: ${(p) => p.theme.textMuted};
`;

const Hint = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.85rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};
`;

const PAGE_SIZE = 20;
const REPLY_MAX_LENGTH = 2000;

const renderStars = (rating: number): React.ReactNode => (
  <Stars aria-label={`${rating} de 5`}>
    {[1, 2, 3, 4, 5].map((position) => (
      <Star key={position} size={14} fill={position <= rating ? 'currentColor' : 'none'} />
    ))}
  </Stars>
);

interface Props {
  canManage: boolean;
}

const ExperienceReviewsPanel: React.FC<Props> = ({ canManage }) => {
  const { showNotification } = useNotification();
  const [page, setPage] = useState(0);
  const [replying, setReplying] = useState<MerchantExperienceReviewDto | null>(null);
  const [reply, setReply] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { data, loading, error, reload } = useAsync(
    () => merchantExperienceApi.reviews({ page, size: PAGE_SIZE }),
    [page]
  );

  const reviews = useMemo(() => data?.content ?? [], [data]);

  /**
   * A média é da PÁGINA carregada, e o rótulo diz isso. O core não expõe um
   * resumo de avaliações de experiência, e anunciar "média da loja" a partir de
   * vinte linhas seria inventar um indicador - justamente o indicador que o
   * card manda não misturar com o da loja.
   */
  const average = reviews.length
    ? reviews.reduce((total, item) => total + item.rating, 0) / reviews.length
    : 0;
  const pending = reviews.filter((item) => !item.reply).length;

  const openReply = (review: MerchantExperienceReviewDto): void => {
    setReplying(review);
    setReply('');
  };

  const submitReply = async (): Promise<void> => {
    if (!replying) return;
    const text = reply.trim();
    if (text === '') return;
    setSubmitting(true);
    try {
      await merchantExperienceApi.replyReview(replying.id, { reply: text });
      showNotification('Resposta publicada.', 'success');
      setReplying(null);
      await reload();
    } catch (err) {
      showNotification(
        extractErrorMessage(
          err,
          'Não foi possível responder a avaliação.',
          EXPERIENCE_REVIEW_ERROR_MESSAGES
        ),
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <StatsGrid>
        <StatCard label="Avaliações nesta página" value={formatNumber(reviews.length)} />
        <StatCard
          label="Média desta página"
          value={reviews.length ? average.toFixed(1).replace('.', ',') : '-'}
        />
        <StatCard label="Sem resposta" value={formatNumber(pending)} />
      </StatsGrid>

      <Notice>
        <Info size={15} />
        <span>
          Estas são as notas das <strong>experiências</strong>. A nota da loja fica em Avaliações e
          as duas médias nunca se somam - são perguntas diferentes.
        </span>
      </Notice>

      <DataState
        loading={loading}
        error={error}
        isEmpty={reviews.length === 0}
        emptyTitle="Nenhuma avaliação ainda"
        emptyDescription="Quem participou de uma experiência pode avaliá-la depois. As notas aparecem aqui."
        onRetry={reload}
      >
        <List>
          {reviews.map((review) => (
            <Item key={review.id}>
              <ItemHead>
                {renderStars(review.rating)}
                <strong>{review.customerName || 'Cliente'}</strong>
                <Badge tone="secondary">{review.experienceTitle}</Badge>
                <span className="when">{formatDateTime(review.createdAt)}</span>
              </ItemHead>

              {review.comment && <Comment>{review.comment}</Comment>}

              {review.reply ? (
                <Reply>
                  <span className="label">
                    Resposta da loja
                    {review.repliedAt ? ` · ${formatDateTime(review.repliedAt)}` : ''}
                  </span>
                  {review.reply}
                </Reply>
              ) : (
                canManage && (
                  <Actions>
                    <Button variant="outline" onClick={() => openReply(review)}>
                      <MessageSquareReply size={16} /> Responder
                    </Button>
                  </Actions>
                )
              )}
            </Item>
          ))}
        </List>

        {(data?.totalPages ?? 0) > 1 && (
          <Pagination
            currentPage={(data?.number ?? 0) + 1}
            totalPages={data?.totalPages ?? 1}
            totalItems={data?.totalElements}
            pageSize={PAGE_SIZE}
            onPageChange={(next) => setPage(next - 1)}
          />
        )}
      </DataState>

      <Modal
        isOpen={replying !== null}
        onClose={() => setReplying(null)}
        title="Responder avaliação"
        width="560px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setReplying(null)} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={submitReply}
              isLoading={submitting}
              disabled={reply.trim().length === 0}
            >
              Publicar resposta
            </Button>
          </>
        }
      >
        <Hint>
          A resposta é publicada uma vez e fica junto da avaliação. O texto do cliente não muda -
          quem escreveu continua dono do que escreveu.
        </Hint>
        {replying?.comment && <Comment>{replying.comment}</Comment>}
        <div style={{ marginTop: '0.85rem' }}>
          <FieldLabel htmlFor="experience-review-reply">Sua resposta</FieldLabel>
          <Textarea
            id="experience-review-reply"
            value={reply}
            maxLength={REPLY_MAX_LENGTH}
            placeholder="Agradeça, responda ao ponto levantado e diga o que muda na próxima edição."
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReply(e.target.value)}
          />
          <Counter>
            {reply.length}/{REPLY_MAX_LENGTH}
          </Counter>
        </div>
      </Modal>
    </>
  );
};

export default ExperienceReviewsPanel;
